import { useState } from 'react';
import { CheckCircle2, Circle, KeyRound } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SaveButton } from '@/components/ui/save-button';
import { useSavedFlash } from '@/hooks/useSavedFlash';
import { useSession } from '@/stores/session';

import { useClearEmbeddingKey, useEmbeddingConfig, useSetEmbeddingKey } from '../queries';

/**
 * Founder/Admin panel to set this organization's own OpenAI key for Knowledge
 * Base embeddings. BYO — there is no platform fallback, so the key must be set
 * here before documents can be indexed or searched. The raw key is never shown;
 * the API reports only configured/masked.
 */
export function EmbeddingKeyCard() {
  const role = useSession((s) => s.role);
  const isAdmin = role === 'founder' || role === 'super_admin';

  const configQuery = useEmbeddingConfig(isAdmin);
  const setKey = useSetEmbeddingKey();
  const clearKey = useClearEmbeddingKey();
  const [draft, setDraft] = useState('');
  const { saved, flash } = useSavedFlash();

  // Non-admins never see this card (endpoint is Founder/Admin only anyway).
  if (!isAdmin) return null;

  const config = configQuery.data;
  const configured = config?.configured ?? false;

  const save = () => {
    const key = draft.trim();
    if (!key) return;
    setKey.mutate(key, {
      onSuccess: () => {
        setDraft('');
        flash();
      },
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="size-4" /> AI Embeddings (OpenAI)
          </CardTitle>
          <CardDescription>
            Your organization&apos;s own OpenAI key, used to index and search the Knowledge
            Base. Billed to your OpenAI account — the platform never pays for it.
          </CardDescription>
        </div>
        {configured ? (
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="size-3.5" /> Configured
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground gap-1">
            <Circle className="size-3.5" /> Not set
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {configured ? (
          <p className="text-muted-foreground text-sm">
            Current key <code className="bg-muted rounded px-1 py-0.5">{config?.masked}</code>
            {config?.model ? <> · model <code className="bg-muted rounded px-1 py-0.5">{config.model}</code></> : null}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">
            No key set — documents will fail to index and search is unavailable until you add one.
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            type="password"
            autoComplete="off"
            placeholder={configured ? 'Paste a new key to replace…' : 'sk-…'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
          <div className="flex gap-2">
            <SaveButton onClick={save} pending={setKey.isPending} saved={saved && !draft.trim()} disabled={!draft.trim()}>
              Save
            </SaveButton>
            {configured ? (
              <Button
                variant="outline"
                onClick={() => clearKey.mutate()}
                disabled={clearKey.isPending}
              >
                Remove
              </Button>
            ) : null}
          </div>
        </div>
        <p className="text-muted-foreground text-xs">
          After saving, re-index from <strong>Knowledge Base → Re-sync base</strong>. The
          embedding model is fixed platform-wide, so only the key is needed here.
        </p>
      </CardContent>
    </Card>
  );
}
